import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
    SHIPMENTS_FEATURE_KEY,
    shipmentsAdapter,
    State,
} from './shipments.reducer';
import * as moment from 'moment';
import { SearchQuery, Shipment } from '../+state/shipments.models';
import { TransportModeHelper, extractValues } from '@qwyk/core';

const toTitleCase = (value: string): string => {
    return value.replace(/\w\S*/g, word => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
};

/**
 * Recomputes the same combined "transport mode + product" text shown in the
 * Product column, so it can be sorted consistently with what the user sees,
 * since it isn't a single field on the shipment/backend model.
 */
const getTransportModeProduct = (shipment: Shipment): string => {
    const rawTransportMode = shipment.data?.transport_mode ?? '';
    const transportMode = toTitleCase(rawTransportMode);

    let product = TransportModeHelper.transform(
        shipment.data?.product ?? '',
        rawTransportMode
    );
    product = product ? product.trim().toUpperCase() : '';

    return `${transportMode} ${product}`.trim();
};

// Lookup the 'Shipments' feature state managed by NgRx
export const getShipmentsState = createFeatureSelector<State>(SHIPMENTS_FEATURE_KEY);

const { selectAll, selectEntities } = shipmentsAdapter.getSelectors();

export const getShipmentsLoading = createSelector(
    getShipmentsState,
    (state: State) => state.loading
);

export const getShipmentsError = createSelector(
    getShipmentsState,
    (state: State) => state.error
);

export const getShipmentsQuery = createSelector(
    getShipmentsState,
    (state: State) => state.query
);

export const getBackendQuery = createSelector(
    getShipmentsState,
    (state: State) => state.backendQuery
);

export const getCurrentSysState = createSelector(
    getBackendQuery,
    (backendQuery) => backendQuery?.sys_state
);

export const getCustomFields = createSelector(
    getShipmentsState,
    (state: State) => state.customFields
);

export const getAllShipments = createSelector(
    getShipmentsState,
    (state: State) => selectAll(state)
);

export const selectAllShipmentsSortedByUpdate = createSelector(
    getAllShipments,
    (shipments) => {
        return [...shipments].sort((a, b) => {
            const dateA = moment(a.updated_at);
            const dateB = moment(b.updated_at);
            return dateB.valueOf() - dateA.valueOf();
        });
    }
);

export const selectAllShipmentsWithFullText = createSelector(
    selectAllShipmentsSortedByUpdate,
    (shipments) => {
        return shipments.map(shipment => {
            const excludeFields = [
                'id',
                'booked_at',
                'created_on',
                'cut_off_date',
                'delivery_date',
                'insurance_date',
                'spotting_date',
                'subscription',
                'updated_at',
                'data.id',
                'last_event.id',
                'last_event.icon',
                'last_event.actual_timestamp',
                'last_event.expected_timestamp',
                'owner.id',
                'owner.team_id',
                'tags'
            ];
            const dataValues = extractValues(shipment, excludeFields);

            const transportModeProduct = getTransportModeProduct(shipment);

            const tags = shipment.tags?.map(tag => tag.attributes.name).join(' ') ?? '';
            const fullText = `${dataValues} ${tags} ${transportModeProduct}`;

            return {
                ...shipment,
                full_text_search: fullText.toLowerCase(),
            };
        });
    }
);

export const getShipmentsEntities = createSelector(
    getShipmentsState,
    (state: State) => selectEntities(state)
);

export const getSelectedId = createSelector(
    getShipmentsState,
    (state: State) => state.selectedId
);

export const getSelected = createSelector(
    getShipmentsEntities,
    getSelectedId,
    (entities, selectedId) => selectedId && entities[selectedId]
);

export const getSysStates = createSelector(
    getShipmentsState,
    (state: State) => state.sysStates
);

export const getSysStatesLoading = createSelector(
    getShipmentsState,
    (state: State) => state.loadingStates
);

export const getBackendPagination = createSelector(
    getShipmentsState,
    (state: State) => state.backendPagination
)

export const selectFilteredShipments = createSelector(
    selectAllShipmentsWithFullText,
    getShipmentsQuery,
    (shipments: Shipment[], query: SearchQuery) => {
        let filteredShipments: Shipment[] = [...shipments];
        if (query && Object.keys(query).length > 0) {
            filteredShipments = filteredShipments.filter(shipment => {
                let matches = true;

                if (query.full) {
                    matches =
                        matches &&
                        shipment['full_text_search']
                            ?.toLowerCase()
                            .includes(query.full.toLowerCase());
                }
                if (query.name) {
                    matches =
                        matches &&
                        shipment.name
                            ?.toLowerCase()
                            .includes(query.name.toLowerCase());
                }
                if (query.organization_shipment_reference) {
                    matches =
                        matches &&
                        shipment.organization_shipment_reference
                            ?.toLowerCase()
                            .includes(
                                query.organization_shipment_reference.toLowerCase()
                            );
                }
                if (query.hbl_number) {
                    matches =
                        matches &&
                        shipment.data?.hbl_number?.toLowerCase() ===
                            query.hbl_number.toLowerCase();
                }
                if (query.mbl_number) {
                    matches =
                        matches &&
                        shipment.data?.mbl_number?.toLowerCase() ===
                            query.mbl_number.toLowerCase();
                }
                if (query.current_milestone) {
                    matches =
                        matches &&
                        shipment.last_event?.key?.toLowerCase() ===
                            query.current_milestone.toLowerCase();
                }
                if (query.eta_range) {
                    const shipmentEta = moment(shipment.data?.eta);
                    const rangeStart = moment(query.eta_range[0]);
                    const rangeEnd = moment(query.eta_range[1]);

                    if (
                        shipmentEta.isValid() &&
                        rangeStart.isValid() &&
                        rangeEnd.isValid()
                    ) {
                        matches =
                            matches &&
                            shipmentEta.isSameOrAfter(rangeStart, 'day') &&
                            shipmentEta.isSameOrBefore(rangeEnd, 'day');
                    } else {
                        matches = false;
                    }
                }
                if (query.vessel) {
                    matches =
                        matches &&
                        shipment.data?.vessel?.toLowerCase() ===
                            query.vessel.toLowerCase();
                }
                if (query.container_number) {
                    matches =
                        matches &&
                        shipment.data?.containers_numbers?.includes(
                            query.container_number
                        );
                }
                if (query.shipper) {
                    matches =
                        matches &&
                        shipment.data?.shipper_name
                            ?.toLowerCase()
                            .includes(query.shipper.toLowerCase());
                }
                if (query.consignee) {
                    matches =
                        matches &&
                        shipment.data?.consignee_name
                            ?.toLowerCase()
                            .includes(query.consignee.toLowerCase());
                }
                if (query.place_of_receipt) {
                    matches =
                        matches &&
                        (shipment.data?.place_of_receipt_locode?.toLowerCase() ===
                            query.place_of_receipt.locode.toLowerCase() ||
                            shipment.data?.place_of_receipt?.name?.toLowerCase().includes(query.place_of_receipt.locode.toLowerCase()));
                }
                if (query.place_of_delivery) {
                    matches =
                        matches &&
                        (shipment.data?.place_of_delivery_locode?.toLowerCase() ===
                            query.place_of_delivery.locode.toLowerCase() ||
                            shipment.data?.place_of_delivery?.name?.toLowerCase().includes(query.place_of_delivery.locode.toLowerCase()));
                }
                if (query.port_of_loading) {
                    matches =
                        matches &&
                        (shipment.data?.port_of_loading_locode?.toLowerCase() ===
                            query.port_of_loading.locode.toLowerCase() ||
                            shipment.data?.port_of_loading?.name?.toLowerCase().includes(query.port_of_loading.locode.toLowerCase()));
                }
                if (query.port_of_discharge) {
                    matches =
                        matches &&
                        (shipment.data?.port_of_discharge_locode?.toLowerCase() ===
                            query.port_of_discharge.locode.toLowerCase() ||
                            shipment.data?.port_of_discharge?.name?.toLowerCase().includes(query.port_of_discharge.locode.toLowerCase()));
                }
                if (query.product_transport_mode) {
                    matches =
                        matches &&
                        `${shipment.data?.transport_mode}:${shipment.data?.product}` ===
                            query.product_transport_mode;
                }
                if (query.team_id?.length) {
                    matches =
                        matches &&
                        query.team_id.includes(shipment.owner?.team_id);
                }
                if (query.tags && query.tags.length > 0) {
                    matches = matches && shipment.tags?.some(tag => query.tags.includes(tag.id));
                }
                if (query.is_subscribed && query.is_subscribed.length > 0) {
                    matches = matches && Boolean(shipment.subscription.shipment_activity_notification || shipment.subscription.shipment_status_notification)
                }

                return matches;
            });

            if (query?.order_by) {
                filteredShipments = [...filteredShipments].sort((a, b) => {
                    const direction =
                        query.order_by_direction === 'desc' ? -1 : 1;

                    const orderBy = query.order_by;

                    let valueA: any;
                    let valueB: any;

                    if (orderBy === 'transport_mode_product') {
                        valueA = getTransportModeProduct(a);
                        valueB = getTransportModeProduct(b);
                    } else {
                        valueA = getNestedProperty(a, orderBy);
                        valueB = getNestedProperty(b, orderBy);
                    }

                    if (isDateField(orderBy)) {
                        valueA = moment(valueA).isValid()
                            ? moment(valueA)
                            : moment(0);
                        valueB = moment(valueB).isValid()
                            ? moment(valueB)
                            : moment(0);
                        return (
                            direction *
                            (valueA.isBefore(valueB)
                                ? -1
                                : valueA.isAfter(valueB)
                                  ? 1
                                  : 0)
                        );
                    }

                    if (
                        typeof valueA === 'string' &&
                        typeof valueB === 'string'
                    ) {
                        return direction * valueA.localeCompare(valueB);
                    }

                    if (
                        typeof valueA === 'number' &&
                        typeof valueB === 'number'
                    ) {
                        return direction * (valueA - valueB);
                    }

                    if (valueA == null && valueB == null) return 0;
                    if (valueA == null) return direction * -1;
                    if (valueB == null) return direction * 1;

                    return (
                        direction * String(valueA).localeCompare(String(valueB))
                    );
                });
            }
        }
        return filteredShipments;
    }
);

const getNestedProperty = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
}

const isDateField = (fieldName: string): boolean => {
    const dateFields = ['eta', 'created_at', 'updated_at', 'departure_date', 'arrival_date'];
    return dateFields.some(field => fieldName.includes(field));
}

export const getFilterSuggestions = createSelector(
    getShipmentsState,
    (state: State) => state.filterSuggestions
);

export const getSuggestionsLoading = createSelector(
    getShipmentsState,
    (state: State) => state.suggestionsLoading
);

export const getShipperSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.shipper_name'] || []
);

export const getConsigneeSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.consignee_name'] || []
);

export const getVesselSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.vessel'] || []
);

export const getPortOfLoadingSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.port_of_loading_locode'] || []
);

export const getPortOfDischargeSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.port_of_discharge_locode'] || []
);

export const getPlaceOfReceiptSuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.place_of_receipt_locode'] || []
);

export const getPlaceOfDeliverySuggestions = createSelector(
    getFilterSuggestions,
    (suggestions) => suggestions?.['data.place_of_delivery_locode'] || []
);
